import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import {
  Button,
  DatePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Eyebrow,
  FormField,
  Input,
  NumberStepper,
  SegmentedControl,
} from '@/shared/components';
import { formatCurrency } from '@/shared/lib/format';
import type { BookingResponse } from '@/shared/api';
import { useUpdateBooking } from './useBookingMutations';
import { useManualPricePreview } from '@/features/properties/manual-booking/useManualPricePreview';
import { ManualPriceSummary } from '@/features/properties/manual-booking/ManualPriceSummary';
import { calcNights } from './helpers';

interface EditBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: BookingResponse;
  guestName: string;
  propertyName: string | null;
}

const dateOnly = (iso: string) => iso.slice(0, 10);

export function EditBookingModal({
  open,
  onOpenChange,
  booking,
  guestName,
  propertyName,
}: EditBookingModalProps) {
  const { t } = useTranslation('manualBooking');
  const [from, setFrom] = useState(() => dateOnly(booking.checkInDate));
  const [to, setTo] = useState(() => dateOnly(booking.checkOutDate));
  const [guests, setGuests] = useState(() => ({
    adults: booking.adults,
    children: booking.children,
    infants: booking.infants,
  }));
  const [nightlyRate, setNightlyRate] = useState(
    () => (booking.agreedAmount ?? booking.baseRate)?.toString() ?? '',
  );

  // Nur beim Öffnen aus dem Booking initialisieren — ein Refetch von `booking` während der
  // Bearbeitung darf laufende, ungespeicherte Eingaben nicht überschreiben.
  const [priceBasis, setPriceBasis] = useState(booking.priceBasis ?? 'PerNight');
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setPriceBasis(booking.priceBasis ?? 'PerNight');
      setFrom(dateOnly(booking.checkInDate));
      setTo(dateOnly(booking.checkOutDate));
      setGuests({
        adults: booking.adults,
        children: booking.children,
        infants: booking.infants,
      });
      setNightlyRate((booking.agreedAmount ?? booking.baseRate)?.toString() ?? '');
    }
    wasOpen.current = open;
  }, [open, booking]);

  const update = useUpdateBooking(booking.id, () => onOpenChange(false));

  const nights = calcNights(from, to);
  const datesValid = nights > 0;
  const rateNum = nightlyRate.trim() !== '' ? Number(nightlyRate) : null;
  const rateValid = rateNum === null || (Number.isFinite(rateNum) && rateNum >= 0);
  const effectiveRate = rateNum ?? booking.baseRate;
  const price = effectiveRate != null ? effectiveRate * nights : (booking.totalPrice ?? 0);
  const agreed = booking.manualMode === 'Direct' || booking.manualMode === 'PaymentLink';
  const quote = useManualPricePreview(
    {
      propertyId: booking.propertyId,
      mode: booking.manualMode === 'Direct' ? 'Direct' : 'PaymentLink',
      basis: priceBasis,
      amount: rateNum ?? booking.agreedAmount ?? 0,
      checkInDate: from,
      checkOutDate: to,
      adults: guests.adults,
      children: guests.children,
    },
    open && agreed && datesValid && rateValid,
    booking.id,
  );
  const canSubmit =
    datesValid &&
    guests.adults >= 1 &&
    rateValid &&
    (!agreed || (Boolean(quote.data) && !quote.isFetching && !quote.isError));

  const title = [guestName, propertyName].filter(Boolean).join(' · ') || 'Buchung';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <Eyebrow variant="muted">Buchung bearbeiten</Eyebrow>
          <DialogTitle className="mt-1">{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Anreise">
              <DatePicker value={from} onChange={(v) => setFrom(v ?? from)} />
            </FormField>
            <FormField label="Abreise">
              <DatePicker value={to} onChange={(v) => setTo(v ?? to)} minDate={from} />
            </FormField>
          </div>

          {/* Die Aufteilung, nicht nur die Zahl: Kleinkinder zahlen keine Kurtaxe, und die
              Rechnung rechnet damit. */}
          <div className="grid grid-cols-3 gap-3">
            <FormField
              label="Erwachsene"
              error={guests.adults < 1 ? 'Mindestens 1 Erwachsener.' : undefined}
            >
              <NumberStepper
                value={guests.adults}
                onChange={(adults) => setGuests((current) => ({ ...current, adults }))}
                min={1}
                max={30}
              />
            </FormField>
            <FormField label="Kinder">
              <NumberStepper
                value={guests.children}
                onChange={(children) => setGuests((current) => ({ ...current, children }))}
                min={0}
                max={30}
              />
            </FormField>
            <FormField label="Kleinkinder" hint="zahlen keine Kurtaxe">
              <NumberStepper
                value={guests.infants}
                onChange={(infants) => setGuests((current) => ({ ...current, infants }))}
                min={0}
                max={30}
              />
            </FormField>
          </div>

          {agreed && (
            <SegmentedControl
              size="touch"
              value={priceBasis}
              onChange={setPriceBasis}
              options={[
                { value: 'PerNight', label: t('perNight') },
                { value: 'TotalStay', label: t('totalStay') },
              ]}
            />
          )}
          <FormField
            label={`${agreed && priceBasis === 'TotalStay' ? t('totalStayLabel') : t('nightPrice')} (${booking.currency})`}
            hint="Leer = unverändert"
            error={!rateValid ? 'Ungültiger Preis (≥ 0).' : undefined}
            className="max-w-48"
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              value={nightlyRate}
              onChange={(e) => setNightlyRate(e.target.value)}
              placeholder="unverändert"
            />
          </FormField>

          {agreed ? (
            <ManualPriceSummary
              quote={quote.data}
              loading={quote.isFetching}
              error={quote.isError}
              onRetry={() => void quote.refetch()}
            />
          ) : (
            <div>
              <Eyebrow variant="muted">Preis gesamt</Eyebrow>
              <div className="border-line bg-card-alt mt-1.5 flex items-center justify-between rounded-md border px-4 py-2.5">
                <span className="text-ink font-serif text-lg font-medium tabular-nums">
                  {formatCurrency(price, booking.currency)}
                </span>
                <span className="text-ink-faint text-xs">
                  {effectiveRate != null
                    ? `${nights} Nächte × ${formatCurrency(effectiveRate, booking.currency)}`
                    : 'aktuell — wird nach Speichern neu berechnet'}
                </span>
              </div>
            </div>
          )}

          <p className="text-ink-soft bg-card-alt border-line/50 flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs leading-relaxed">
            <Info size={14} strokeWidth={2} className="text-ink-faint mt-0.5 shrink-0" />
            Datums-Änderungen aktualisieren Belegung & Rechnung und geben in Channex die betroffenen
            Nächte frei bzw. belegen sie neu (nur die Änderungen).
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="brand"
            disabled={!canSubmit}
            loading={update.isPending}
            onClick={() =>
              update.mutate({
                checkInDate: from,
                checkOutDate: to,
                adults: guests.adults,
                children: guests.children,
                infants: guests.infants,
                nightlyRate: agreed ? null : rateNum,
                ...(agreed
                  ? {
                      priceBasis,
                      agreedAmount: rateNum ?? booking.agreedAmount ?? 0,
                      expectedPriceIdentity: quote.data?.identity,
                    }
                  : {}),
              })
            }
          >
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
